import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Trash2, Loader2, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import DatePicker from "../../components/os/DatePicker";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";

export default function Onboarding() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee: "", role: "", start_date: "" });

  const load = () => api.get("/px/onboarding").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.employee.trim()) return toast.error("El colaborador es obligatorio");
    try { await api.post("/px/onboarding", f); setOpen(false); setF({ employee: "", role: "", start_date: "" }); load(); toast.success("Plan creado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggle = async (o, t) => {
    try { const r = await api.post(`/px/onboarding/${o.id}/toggle/${t.id}`); setItems((prev) => prev.map((x) => x.id === o.id ? { ...x, tasks: r.data.tasks, progress: r.data.progress } : x)); }
    catch { /* */ }
  };
  const del = async (o) => { try { await api.delete(`/px/onboarding/${o.id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA Onboarding">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="onboarding-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="overline text-navy/40 mb-2">OMNIA People Suite · Core HR</div>
            <h2 className="font-editorial text-4xl tracking-tight">OMNIA Onboarding</h2>
            <p className="mt-2 text-navy/50">Planes de incorporación con checklist y avance por colaborador.</p>
          </div>
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="onb-new"><Plus className="w-4 h-4" /> Nuevo plan</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin planes de onboarding.</div> :
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((o) => (
              <motion.div key={o.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5" data-testid={`onb-${o.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-violet text-white flex items-center justify-center text-sm">{o.employee[0]}</div>
                  <div className="min-w-0"><div className="font-medium truncate">{o.employee}</div><div className="text-[11px] text-navy/40 truncate">{o.role || "—"}{o.start_date ? ` · ${o.start_date}` : ""}</div></div>
                  <button onClick={() => del(o)} className="ml-auto text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2 mb-3"><div className="flex-1 h-2 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-violet transition-all" style={{ width: `${o.progress}%` }} /></div><span className="text-xs text-navy/50 w-9 text-right">{o.progress}%</span></div>
                <div className="space-y-1.5">
                  {o.tasks.map((t) => (
                    <button key={t.id} onClick={() => toggle(o, t)} className="w-full flex items-center gap-2 text-left text-sm py-1 group" data-testid={`onb-task-${t.id}`}>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${t.done ? "bg-violet border-violet text-white" : "border-navy/20 group-hover:border-violet"}`}>{t.done && <Check className="w-3 h-3" />}</span>
                      <span className={t.done ? "line-through text-navy/40" : "text-navy/75"}>{t.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onb-dialog">
          <div className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl flex items-center gap-2"><UserPlus className="w-5 h-5" /> Nuevo plan</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5 text-navy/40" /></button></div>
            <div className="space-y-3">
              <input className={input} placeholder="Nombre del colaborador" value={f.employee} onChange={(e) => setF({ ...f, employee: e.target.value })} data-testid="onb-employee" />
              <input className={input} placeholder="Puesto" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
              <DatePicker value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} placeholder="Fecha de ingreso" />
              <p className="text-xs text-navy/40">Se crea con una checklist estándar de incorporación (editable marcando tareas).</p>
            </div>
            <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="onb-submit">Crear</button></div>
          </div>
        </div>
      )}
    </OSLayout>
  );
}
